import React, { useState } from "react";
import { Download, Share, PlusSquare, X, Smartphone, Bell, Zap } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import usePwaInstall from "@/hooks/usePwaInstall";
import { toast } from "sonner";

function Step({ n, icon: Icon, children }) {
  return (
    <li className="flex items-start gap-3">
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#2F4A3A] font-display text-sm font-semibold text-white">{n}</span>
      <span className="pt-1 text-sm leading-relaxed text-[#1F2A24]">{children} {Icon && <Icon size={16} className="inline align-text-bottom text-[#2F4A3A]" />}</span>
    </li>
  );
}

export function InstallGuideDialog({ open, onOpenChange, mode }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl bg-[#FFFDF9] sm:max-w-md" data-testid="install-guide-dialog">
        <DialogHeader>
          <img src="/icons/icon-192.png" alt="Douceur" className="mb-2 h-16 w-16 rounded-2xl shadow-md" />
          <DialogTitle className="font-display text-2xl">Installer Douceur</DialogTitle>
          <DialogDescription>Ajoute l'app sur ton écran d'accueil : elle s'ouvre en plein écran comme une vraie application.</DialogDescription>
        </DialogHeader>
        {mode === "ios" ? (
          <ol className="space-y-4" data-testid="install-steps-ios">
            <Step n={1}>Ouvre cette page dans <b>Safari</b></Step>
            <Step n={2} icon={Share}>Appuie sur le bouton <b>Partager</b></Step>
            <Step n={3} icon={PlusSquare}>Choisis <b>« Sur l'écran d'accueil »</b>, puis <b>Ajouter</b></Step>
            <Step n={4} icon={Bell}>Ouvre Douceur depuis l'icône et active tes <b>rappels</b> dans Progrès</Step>
          </ol>
        ) : (
          <ol className="space-y-4" data-testid="install-steps-manual">
            <Step n={1}>Ouvre le <b>menu</b> de ton navigateur (⋮ ou …)</Step>
            <Step n={2} icon={Download}>Choisis <b>« Installer l'application »</b> ou <b>« Ajouter à l'écran d'accueil »</b></Step>
            <Step n={3}>Retrouve Douceur parmi tes applications</Step>
          </ol>
        )}
        <div className="grid grid-cols-3 gap-2 pt-2 text-center text-[11px] text-[#6B7A70]">
          <div className="rounded-2xl bg-[#EAF0E6] p-3"><Zap size={18} className="mx-auto mb-1 text-[#2F4A3A]" />SOS en 1 geste</div>
          <div className="rounded-2xl bg-[#EAF0E6] p-3"><Bell size={18} className="mx-auto mb-1 text-[#2F4A3A]" />Rappels doux</div>
          <div className="rounded-2xl bg-[#EAF0E6] p-3"><Smartphone size={18} className="mx-auto mb-1 text-[#2F4A3A]" />Plein écran</div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function useInstallAction() {
  const pwa = usePwaInstall();
  const [guideOpen, setGuideOpen] = useState(false);
  const install = async () => {
    if (pwa.mode === "native") {
      const ok = await pwa.promptInstall();
      if (ok) toast.success("Douceur est installée sur ton appareil !");
      return;
    }
    setGuideOpen(true);
  };
  return { ...pwa, install, guideOpen, setGuideOpen };
}

export function InstallButton({ className = "", label = "Installer l'app", testid = "install-app-btn" }) {
  const a = useInstallAction();
  if (a.installed) return null;
  return (
    <>
      <button onClick={a.install} data-testid={testid} className={className}>
        <Download size={16} /> {label}
      </button>
      <InstallGuideDialog open={a.guideOpen} onOpenChange={a.setGuideOpen} mode={a.mode} />
    </>
  );
}

export function InstallBanner() {
  const a = useInstallAction();
  if (a.installed || a.dismissed) return null;
  return (
    <div className="fade-up flex items-center gap-4 rounded-3xl border border-[#E9E1D6] bg-white p-4 pr-3 md:p-5" data-testid="install-banner">
      <img src="/icons/icon-192.png" alt="" className="h-12 w-12 shrink-0 rounded-2xl shadow-sm" />
      <div className="min-w-0 flex-1">
        <div className="font-semibold">Installe Douceur sur ton téléphone</div>
        <div className="text-sm text-[#6B7A70]">Accès instantané au mode SOS et rappels doux, même app fermée.</div>
      </div>
      <button onClick={a.install} data-testid="install-banner-btn" className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#2F4A3A] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#243A2D]">
        <Download size={15} /> <span className="hidden sm:inline">Installer</span>
      </button>
      <button onClick={a.dismiss} aria-label="Masquer" data-testid="install-banner-dismiss" className="shrink-0 rounded-full p-3 text-[#B0B8B3] transition-colors hover:bg-[#F4EFE7] hover:text-[#4A5A50]"><X size={16} /></button>
      <InstallGuideDialog open={a.guideOpen} onOpenChange={a.setGuideOpen} mode={a.mode} />
    </div>
  );
}
